import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from '@components/common/Button/Button.component'
import { useCampaignCalendar } from './useCampaignCalendar'
import { format } from 'date-fns'
import { Calendar } from '@components/common'
import {
  Agenda,
  DragAndDrop,
  Inject,
  Month,
  Resize,
  ScheduleComponent,
} from '@syncfusion/ej2-react-schedule'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import AddCampaignModal from '@components/layout/Modals/AddCampaign/AddCampaign.component'
import MarketingDeleteCampaignModal from '../../MarketingDeleteCampaignModal.component'
import { Table } from 'react-bootstrap'
import { formatToHHmm } from '@utils/marketing.utils'
import './CampaignCalendarStyle.css'
import RescheduleCampaignModal from '../../modal/RescheduleCampaign/RescheduleCampaign.component'
import RescheduleCampaignModal2 from '../../modal/RescheduleCampaign2/RescheduleCampaign2.component'

const CAMPAIGN_CALENDAR_NAV_ANCHOR_ID = 'mmc-campaign-calendar-nav-anchor'

interface CampaignCalendarProps {
  /** When set (e.g. filtered list from parent), calendar shows only these campaigns */
  campaigns?: ICampaignRequest[]
}

export interface ITemplateProps {
  id: string
  headId: string
  isHead: boolean
  subject: string
  startTime: Date | string
  endTime: Date | string
  color: string
  campaign: ICampaignRequest
}

const CampaignCalendar: React.FC<CampaignCalendarProps> = ({ campaigns }) => {
  const hooks = useCampaignCalendar(campaigns)
  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    let cancelled = false
    let frames = 0
    const attach = (): void => {
      const el = document.getElementById(CAMPAIGN_CALENDAR_NAV_ANCHOR_ID)
      if (el) {
        setNavAnchor(el)
        return
      }
      frames += 1
      if (!cancelled && frames < 90) {
        requestAnimationFrame(attach)
      }
    }
    attach()
    return () => {
      cancelled = true
      setNavAnchor(null)
    }
  }, [])

  const campaignTemplate = (p: ITemplateProps): JSX.Element => {
    const className =
      p.headId === hooks.selectedCampaign?.campaign_id ? ' active' : ''
    let icon = 'mgc_message_4_fill'
    if (p.campaign.channel === 'Email') icon = 'mgc_mail_fill'
    if (p.campaign.channel === 'Facebook') icon = 'mgc_facebook_fill'
    return p.isHead ? (
      <div className={`cc-event-tile${className}`}>
        <div className="cc-event-header-container">
          <div className="cc-event-header">
            <span
              className={`channel-icon ${icon}`}
              data-bs-toggle="tooltip"
              title={p.campaign.channel}
            />
            <span>
              {p.campaign.start_time && formatToHHmm(p.campaign.start_time)}
            </span>
          </div>
          <DropDownButtonComponent
            items={[
              { id: 'edit', text: 'Edit', iconCss: 'mgc_pencil_fill' },
              {
                id: 'reschedule',
                text: 'Reschedule',
                iconCss: 'mgc_calendar_time_add_fill',
              },
              { id: 'delete', text: 'Delete', iconCss: 'mgc_delete_2_fill' },
            ]}
            cssClass="e-caret-hide campaign-event-tile-action-btn"
            iconCss="mgc_more_1_fill"
            beforeOpen={(e) => {
              e.event?.stopPropagation()
            }}
            select={(args) => hooks.onDropDownSelect(args, p.campaign)}
          />
        </div>
        <div className="cc-event-body">{p.subject}</div>
      </div>
    ) : (
      <span className="cc-event-child">{p.subject}</span>
    )
  }

  const previewColumn: string[][] = [
    ['Campaign name', 'Description', 'Campaign type', 'Template'],
    ['Start date', 'End date', 'Sending option'],
    ['Schedule date'], // for "Later" sending option
    ['Recurring interval'], // for "Recurring" sending option
    ['Recurring days', 'Send at', 'Duration'], // for "Recurring" weekly
    ['Recurring option'], // for "Recurring" monthly
  ]

  const displayText = (props: ICampaignRequest, title: string): string => {
    if (title === 'Campaign name') return props.name
    if (title === 'Description') return props.description || '-'
    if (title === 'Campaign type')
      return props.type ? (props.type as string) : '-'
    if (title === 'Template')
      return props.campaign_template ? props.campaign_template.name : '-'
    if (title === 'Start date' && props.start_date && props.start_time) {
      const startDate = new Date(props.start_date)
      const startTime = props.start_time.split(':').map(Number)
      startDate.setHours(startTime[0], startTime[1], 0, 0)
      return format(startDate, 'MM/dd/yy - h:mm a').toUpperCase()
    }
    if (title === 'End date' && props.end_date && props.end_time) {
      const endDate = new Date(props.end_date)
      const endTime = props.end_time.split(':').map(Number)
      endDate.setHours(endTime[0], endTime[1], 0, 0)
      return format(endDate, 'MM/dd/yy - h:mm a').toUpperCase()
    }
    if (title === 'Sending option') return props.sending_option || '-'
    if (
      title === 'Schedule date' &&
      props.schedule_date &&
      props.schedule_time
    ) {
      const scheduleDate = new Date(props.schedule_date)
      const scheduleTime = props.schedule_time.split(':').map(Number)
      scheduleDate.setHours(scheduleTime[0], scheduleTime[1], 0, 0)
      return format(scheduleDate, 'MM/dd/yy - h:mm a').toUpperCase()
    }
    if (title === 'Recurring interval' && props.recurring_interval) {
      return props.recurring_interval
    }
    if (title === 'Recurring days')
      return props.recurring_days
        ? props.recurring_days.replaceAll(',', ', ')
        : '-'
    if (title === 'Send at' && props.recurring_time) {
      return formatToHHmm(props.recurring_time)
    }
    if (
      title === 'Duration' &&
      props.recurring_duration &&
      props.recurring_interval
    ) {
      return (
        props.recurring_duration + props.recurring_interval.toLowerCase()
      ).slice(0, -2)
    }

    return '-'
  }

  const tooltipTemplate = (props: ITemplateProps): JSX.Element => {
    return (
      <div className="campaign-event-tile-tooltip">
        <div className="cett-header">Campaign info</div>
        <div className="border"></div>
        <div>
          <Table className="campaign-tooltip">
            <tbody>
              {previewColumn[0].map((title) => (
                <tr key={title}>
                  <th>{title}</th>
                  <td>{displayText(props.campaign, title)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="cett-header">Scheduling</div>
        <div className="border"></div>
        <div>
          <Table className="campaign-tooltip">
            <tbody>
              {previewColumn[1].map((title) => (
                <tr key={title}>
                  <th>{title}</th>
                  <td>{displayText(props.campaign, title)}</td>
                </tr>
              ))}
              {props.campaign.sending_option === 'Later' &&
                previewColumn[2].map((title) => (
                  <tr key={title}>
                    <th>{title}</th>
                    <td>{displayText(props.campaign, title)}</td>
                  </tr>
                ))}
              {props.campaign.sending_option === 'Recurring' &&
                previewColumn[3].map((title) => (
                  <tr key={title}>
                    <th>{title}</th>
                    <td>{displayText(props.campaign, title)}</td>
                  </tr>
                ))}
              {props.campaign.sending_option === 'Recurring' &&
                props.campaign.recurring_interval === 'Week' &&
                previewColumn[4].map((title) => (
                  <tr key={title}>
                    <th>{title}</th>
                    <td>{displayText(props.campaign, title)}</td>
                  </tr>
                ))}
              {props.campaign.sending_option === 'Recurring' &&
                props.campaign.recurring_interval === 'Month' &&
                previewColumn[5].map((title) => (
                  <tr key={title}>
                    <th>{title}</th>
                    <td>{displayText(props.campaign, title)}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>
    )
  }

  const calendarNavBar = (
    <>
      <div ref={hooks.calendarContainerRef}>
        <Button
          className="button-date button-secondary"
          leftIcon={<i className="button-date mgc_calendar_2_fill"></i>}
          rightIcon={<i className="button-date mgc_down_small_fill"></i>}
          onClick={hooks.toggleCalendar}
          size={'md'}
        >
          {format(new Date(hooks.selectedDate), 'MMMM yyyy')}
        </Button>
        {hooks.showCalendar && (
          <Calendar
            value={new Date(hooks.selectedDate)}
            onChange={hooks.onDateChange}
          />
        )}
      </div>
      <div className="mmc-arrow-navigator">
        <button
          type="button"
          className="unstyled-button ti ti-chevron-left"
          onClick={hooks.handlePrevMonth}
        />
        <button
          type="button"
          className="unstyled-button ti ti-chevron-right"
          onClick={hooks.handleNextMonth}
        />
      </div>
    </>
  )

  return (
    <>
      {navAnchor ? createPortal(calendarNavBar, navAnchor) : null}
      <div className="mc-campaign-calendar">
        <ScheduleComponent
          allowDragAndDrop={true}
          allowResizing={true}
          currentView="Month"
          cssClass="mc-cc-calendar"
          height="672px"
          dataBound={hooks.onDataBound}
          drag={hooks.onDrag}
          dragStart={hooks.onDragStart}
          dragStop={hooks.onDragStop}
          eventRendered={hooks.onEventRendered}
          eventSettings={{
            dataSource: hooks.visibleEvents,
            enableTooltip: true,
            fields: {
              id: 'id',
              subject: { name: 'subject' },
              startTime: { name: 'startTime' },
              endTime: { name: 'endTime' },
            },
            template: campaignTemplate,
            tooltipTemplate: tooltipTemplate,
          }}
          popupOpen={hooks.handlePopupOpen}
          ref={hooks.scheduleRef}
          renderCell={hooks.renderDayHeader}
          resizing={hooks.onResizing}
          resizeStart={hooks.onResizing}
          resizeStop={hooks.onResizing}
          rowAutoHeight={true}
          selectedDate={new Date(hooks.selectedDate)}
        >
          <Inject services={[Month, Agenda, DragAndDrop, Resize]} />
        </ScheduleComponent>
      </div>
      <MarketingDeleteCampaignModal
        show={hooks.showDeleteModal}
        campaignName={hooks.selectedCampaign?.name ?? ''}
        onCancel={hooks.onHideModal}
        onDelete={hooks.onConfirmDelete}
      />
      {hooks.showEditCampaignModal && hooks.selectedCampaign && (
        <AddCampaignModal
          initialCampaign={hooks.selectedCampaign}
          show={hooks.showEditCampaignModal}
          onHide={hooks.onHideModal}
        />
      )}
      {hooks.showRescheduleDragModal && hooks.selectedCampaign && (
        <RescheduleCampaignModal
          campaign={hooks.selectedCampaign}
          show={hooks.showRescheduleDragModal}
          onHide={hooks.onCancelReschedule}
          onConfirm={hooks.onConfirmReschedule}
          setTime={hooks.setRescheduleTime}
          time={hooks.rescheduleTime}
        />
      )}
      {hooks.showRescheduleDropDownModal && hooks.selectedCampaign && (
        <RescheduleCampaignModal2
          campaign={hooks.selectedCampaign}
          show={hooks.showRescheduleDropDownModal}
          onHide={hooks.onHideModal}
        />
      )}
    </>
  )
}

export default CampaignCalendar
